import React, { forwardRef } from 'react';
import {
    array,
    arrayOf,
    bool,
    func,
    number,
    object,
    oneOfType,
    shape,
    string
} from 'prop-types';
import clsx from 'clsx';
import { deburr } from 'lodash';

import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';

import GroupHeader from './GroupHeader';
import Suggestion from './Suggestion';

import defaultClasses from './Autocomplete.module.css';

function getSuggestions(
    groupedOptions,
    inputValue,
    { hideSelected, itemToString, maxSuggestions, selectedItemLabels }
) {
    const trimmedInputValue = deburr(inputValue.trim()).toLowerCase();
    const inputLength = trimmedInputValue.length;
    // Don't restrict group count if the menu is open and the user has not
    // searched for a value
    const maxCount = inputLength ? maxSuggestions : 0;

    return groupedOptions.map(({ items, ...group }) => {
        let count = 0;
        items || (items = []);
        return {
            ...group,
            items: items.filter(suggestion => {
                const itemLabel = itemToString(suggestion).toLowerCase();
                if (hideSelected && selectedItemLabels.includes(itemLabel)) {
                    return false;
                }
                if (maxCount && count >= maxCount) {
                    return false;
                }
                const display = itemLabel.includes(trimmedInputValue);

                if (display) {
                    count += 1;
                }

                return display;
            })
        };
    });
}

const SuggestionList = forwardRef(({
    classes,
    getItemProps,
    groupedOptions,
    hideSelected,
    highlightedIndex,
    inputValue,
    isOpen,
    itemToKey,
    itemToString,
    loading,
    maxSuggestions,
    selectedItem,
    popperNode,
    onKeyDown,
    searchRan,
    ...props
}, ref) => {
    let selectedItemLabels = itemToString(selectedItem);
    if (Array.isArray(selectedItem)) {
        selectedItemLabels = selectedItem.map(item => itemToString(item).toLowerCase());
    }

    const groupedSuggestions = getSuggestions(
        groupedOptions,
        inputValue,
        { hideSelected, itemToString, maxSuggestions, selectedItemLabels }
    );

    const results = [];
    groupedSuggestions.forEach(suggestionGroup => {
        suggestionGroup.items.forEach(suggestion => {
            results.push(suggestion);
        });
    });

    // Inject an empty object to populate results if loading is true, this enables
    // the "Loading..." menu option during async processes.
    // Note: "loading" only used when AutoComponent used as a search component.
    // 2nd Note: yes I know this is hacky.
    if (loading) {
        results.push({});
    }

    let suggestionsIndex = 0;

    const moreResults = (key = 0) => {
        return (
            <React.Fragment key={key}>
                <Divider
                    className={clsx(
                        defaultClasses.optGroupDivider,
                        classes.optGroupDivider
                    )}
                    component="li"
                />
                <MenuItem
                    disabled
                >
                    Hit Enter for more results.
                </MenuItem>
            </React.Fragment>
        );
    };

    return (
        <Popper
            anchorEl={popperNode.current}
            open={isOpen}
            popperRef={innerRef => ref(innerRef ? innerRef : popperNode)}
            placement="bottom"
            className={clsx(defaultClasses.popper, classes.popperOverride)}
        >
            <div {...props}>
                <Paper
                    classes={{
                        root: clsx(
                            defaultClasses.dropdownMenu,
                            classes.dropdownMenu
                        )
                    }}
                    style={{
                        width: popperNode.current && popperNode.current.clientWidth
                    }}
                    square
                >
                    {
                        !results.length &&
                        <ul
                            className={clsx(
                                defaultClasses.optGroup,
                                classes.optGroup
                            )}
                        >
                            <MenuItem disabled>
                                {
                                    onKeyDown ? 'Hit Enter to search.' : 'No results found.'
                                }
                            </MenuItem>
                        </ul>
                    }
                    {results.length > 0 && groupedSuggestions.map(
                        ({ isLoading, items, name, itemProps }, index) => {
                            let menuItems = [];
                            if (isLoading || loading) {
                                menuItems.push(
                                    <MenuItem
                                        classes={{
                                            root: clsx(
                                                defaultClasses.optGroupProgress,
                                                classes.optGroupProgress
                                            )
                                        }}
                                        disabled
                                        key={index}
                                    >
                                        <CircularProgress
                                            classes={{
                                                root: clsx(
                                                    defaultClasses.optGroupProgressIndicator,
                                                    classes.optGroupProgressIndicator
                                                )
                                            }}
                                            size={12}
                                            thickness={6}
                                        />
                                        Loading...
                                    </MenuItem>
                                );
                            } else {
                                menuItems = items.map((item) => {
                                    const index = suggestionsIndex++;
                                    if (!item.length) {
                                        item = {
                                            ...itemProps,
                                            ...item
                                        };
                                    }
                                    return (
                                        <Suggestion
                                            key={itemToKey(item, index)}
                                            index={index}
                                            itemProps={getItemProps({ item })}
                                            highlightedIndex={highlightedIndex}
                                            label={itemToString(item)}
                                            selectedItem={selectedItem}
                                            selectedItemLabels={selectedItemLabels}
                                        />
                                    );
                                });

                                if (onKeyDown && !searchRan) {
                                    const index = items.length + 1;
                                    menuItems.push(moreResults(index));
                                }
                            }
                            return (
                                <ul
                                    className={clsx(
                                        defaultClasses.optGroup,
                                        classes.optGroup
                                    )}
                                    key={name || index}
                                >
                                    {
                                        menuItems.length > 0 &&
                                        <React.Fragment>
                                            <GroupHeader>
                                                {name}
                                            </GroupHeader>
                                            {menuItems}
                                            <Divider
                                                className={clsx(
                                                    defaultClasses.optGroupDivider,
                                                    classes.optGroupDivider
                                                )}
                                                component="li"
                                            />
                                        </React.Fragment>
                                    }
                                </ul>
                            );
                        }
                    )}
                </Paper>
            </div>
        </Popper>
    );
});

SuggestionList.displayName = 'SuggestionList';

SuggestionList.defaultProps = {
    getItemProps:   () => {},
    maxSuggestions: 0
};

SuggestionList.propTypes = {
    classes:        object,
    getItemProps:   func,
    groupedOptions: arrayOf(shape({
        name:      string,
        items:     array,
        isLoading: bool,
        itemProps: object
    })),
    hideSelected:     bool,
    highlightedIndex: number,
    inputValue:       string,
    isOpen:           bool,
    itemToKey:        func.isRequired,
    itemToString:     func.isRequired,
    maxSuggestions:   number,
    selectedItem:     oneOfType([array, object, number, string]),
    popperNode:       shape({
        current: object
    }).isRequired
};

export default SuggestionList;
